import React from 'react';
import { theme } from '../../theme';
import { formatDate } from '../../utils/date';

export type SearchFilterContext = 'sessions' | 'testPlans' | 'features';

export interface SearchFilterBarProps {
  context: SearchFilterContext;
  filterText: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  filterMilestone?: string;
  availableMilestones?: string[];
  milestoneMetadata?: Record<string, { startDate: string; endDate?: string }>;
  onSortFieldChange?: (field: string) => void;
  onSortDirectionChange?: (direction: 'asc' | 'desc') => void;
  onFilterTextChange: (text: string) => void;
  onFilterMilestoneChange?: (milestone: string) => void;
  filterPlaceholder?: string;
  showSort?: boolean; // Optional flag to show/hide sort controls
  // Completed date range filter
  filterCompletedFrom?: string;
  filterCompletedTo?: string;
  onFilterCompletedFromChange?: (date: string) => void;
  onFilterCompletedToChange?: (date: string) => void;
  showCompletedDateFilter?: boolean;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  context,
  filterText,
  sortField,
  sortDirection,
  filterMilestone = '',
  availableMilestones = [],
  milestoneMetadata = {},
  onSortFieldChange,
  onSortDirectionChange,
  onFilterTextChange,
  onFilterMilestoneChange,
  filterPlaceholder,
  showSort = true, // Default to showing sort controls
  filterCompletedFrom = '',
  filterCompletedTo = '',
  onFilterCompletedFromChange,
  onFilterCompletedToChange,
  showCompletedDateFilter = false
}) => {
  const getSortOptions = () => {
    switch (context) {
      case 'sessions':
        return [
          { value: 'id', label: 'ID' },
          { value: 'user', label: 'User' },
          { value: 'createdAt', label: 'Created Date' },
          { value: 'sessionType', label: 'Session Type' },
          { value: 'deadline', label: 'Deadline' },
          { value: 'milestone', label: 'Milestone' }
        ];
      case 'testPlans':
        return [
          { value: 'title', label: 'Title' },
          { value: 'createdAt', label: 'Created Date' },
          { value: 'milestone', label: 'Milestone' }
        ];
      case 'features':
        return [
          { value: 'alphabetical', label: 'Alphabetical' },
          { value: 'createdAt', label: 'Created Date' }
        ];
      default:
        return [];
    }
  };

  const getDefaultPlaceholder = () => {
    switch (context) {
      case 'sessions':
        return 'Search by ID, user, type, platform, milestone...';
      case 'testPlans':
        return 'Search by title, platforms, milestone...';
      case 'features':
        return 'Search by title, description, feature, component, test case...';
      default:
        return 'Search...';
    }
  };

  const showMilestoneFilter = context === 'sessions' || context === 'testPlans';

  return (
    <div style={{ 
      display: 'flex', 
      gap: '1rem', 
      marginBottom: '1.5rem',
      padding: '1rem',
      backgroundColor: theme.surface,
      borderRadius: '8px',
      border: `1px solid ${theme.gridLine}`,
      alignItems: 'center',
      flexWrap: 'wrap'
    }}>
      {/* Sort By - Only show if showSort is true and callbacks are provided */}
      {showSort && sortField && sortDirection && onSortFieldChange && onSortDirectionChange && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontWeight: '600', fontSize: '0.875rem', color: theme.textPrimary }}>Sort by:</label>
          <select
            value={sortField}
            onChange={(e) => onSortFieldChange(e.target.value)}
            style={{
              padding: '0.5rem 2.5rem 0.5rem 0.75rem',
              borderRadius: '4px',
              border: `1px solid ${theme.gridLine}`,
              fontSize: '0.875rem',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
            }}
          >
            {getSortOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '4px',
              border: `1px solid ${theme.gridLine}`,
              backgroundColor: theme.surfaceElevated,
              color: theme.cyanGlow,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
            title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      )}

      {/* Milestone Filter (only for sessions and test plans) */}
      {showMilestoneFilter && onFilterMilestoneChange && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontWeight: '600', fontSize: '0.875rem', color: theme.textPrimary }}>Milestone:</label>
          <select
            value={filterMilestone}
            onChange={(e) => onFilterMilestoneChange(e.target.value)}
            style={{
              padding: '0.5rem 2.5rem 0.5rem 0.75rem',
              borderRadius: '4px',
              border: `1px solid ${theme.gridLine}`,
              fontSize: '0.875rem',
              cursor: 'pointer',
              minWidth: '150px',
              appearance: 'none',
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
            }}
          >
            <option value="">All Milestones</option>
            {availableMilestones.map(milestone => {
              const metadata = milestoneMetadata[milestone];
              const startDate = metadata?.startDate ? formatDate(metadata.startDate, 'MMM dd, yyyy') : null;
              const endDate = metadata?.endDate ? formatDate(metadata.endDate, 'MMM dd, yyyy') : null;
              const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : startDate ? `Started: ${startDate}` : null;
              const displayText = dateRange ? `${milestone} (${dateRange})` : milestone;
              return (
                <option key={milestone} value={milestone}>{displayText}</option>
              );
            })}
          </select>
        </div>
      )}

      {/* Completed Date Range Filter */}
      {showCompletedDateFilter && onFilterCompletedFromChange && onFilterCompletedToChange && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontWeight: '600', fontSize: '0.875rem', color: theme.textPrimary }}>Completed:</label>
          <input
            type="date"
            value={filterCompletedFrom}
            onChange={(e) => onFilterCompletedFromChange(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: `1px solid ${theme.gridLine}`,
              fontSize: '0.875rem',
              backgroundColor: theme.surfaceElevated,
              color: theme.textPrimary
            }}
            title="From date"
          />
          <span style={{ color: theme.textMuted }}>to</span>
          <input
            type="date"
            value={filterCompletedTo}
            onChange={(e) => onFilterCompletedToChange(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: `1px solid ${theme.gridLine}`,
              fontSize: '0.875rem',
              backgroundColor: theme.surfaceElevated,
              color: theme.textPrimary
            }}
            title="To date"
          />
          {(filterCompletedFrom || filterCompletedTo) && (
            <button
              onClick={() => {
                onFilterCompletedFromChange('');
                onFilterCompletedToChange('');
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: `1px solid ${theme.gridLine}`,
                backgroundColor: theme.surfaceElevated,
                color: theme.textMuted,
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
              title="Clear date filter"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Filter/Search Input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
        <label style={{ fontWeight: '600', fontSize: '0.875rem', color: theme.textPrimary }}>Filter:</label>
        <input
          type="text"
          value={filterText}
          onChange={(e) => onFilterTextChange(e.target.value)}
          placeholder={filterPlaceholder || getDefaultPlaceholder()}
          style={{
            flex: 1,
            padding: '0.5rem',
            borderRadius: '4px',
            border: `1px solid ${theme.gridLine}`,
            fontSize: '0.875rem',
            minWidth: '200px',
            backgroundColor: theme.surfaceElevated,
            color: theme.textPrimary
          }}
        />
      </div>
    </div>
  );
};

export default SearchFilterBar;

